import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Typography,
  Button,
  Tooltip,
  styled,
  alpha,
  InputAdornment,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getLendersList } from '../../services/proposalService';
import { toast } from 'react-toastify';

const TableWrapper = styled(Box)(({ theme }) => ({
  '& .MuiTableContainer-root': {
    borderRadius: '16px',
    border: '1px solid rgba(229, 232, 235, 0.7)',
    boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.04)',
    overflow: 'auto',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.06)'
    },
    '&::-webkit-scrollbar': {
      height: '8px',
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
      '&:hover': {
        background: '#666'
      }
    }
  }
}));

const TableHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2.5, 3),
  backgroundColor: theme.palette.background.paper,
  borderBottom: '1px solid rgba(229, 232, 235, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '280px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    borderRadius: '12px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
      fontSize: '14px'
    },
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light
      }
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
        borderColor: theme.palette.primary.main
      }
    }
  }
}));

const SelectionInfo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderBottom: '1px solid rgba(229, 232, 235, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2)
}));

const SelectionHelp = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderBottom: '1px solid rgba(229, 232, 235, 0.7)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontSize: '13px'
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px',
  '&.MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    borderBottom: '2px solid rgba(229, 232, 235, 0.7)'
  },
  '&.MuiTableCell-body': {
    color: theme.palette.text.primary,
    fontSize: '14px',
    letterSpacing: '0.01em',
    borderBottom: '1px solid rgba(229, 232, 235, 0.5)',
    '& .email': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: theme.palette.primary.dark
      }
    }
  }
}));

const BulkActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 500,
  padding: theme.spacing(0.5, 1),
  minWidth: 'auto',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08)
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: '16px',
  border: '1px dashed rgba(0, 0, 0, 0.12)'
}));

const LenderSelectionTable = ({ selectedLenders, onSelectionChange }) => {
  const [lenders, setLenders] = useState([]);
  const [filteredLenders, setFilteredLenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showSelectionHelp, setShowSelectionHelp] = useState(true);

  const filterFields = [
    { label: 'Name', value: 'name' },
    { label: 'Institution Type', value: 'institutionType' },
    { label: 'Location', value: 'location' },
    { label: 'Type of Lending', value: 'lendingType' },
    { label: 'Retail Loans', value: 'retailLoans' }
  ];
  const [activeFilters, setActiveFilters] = useState([]); // [{ field: 'name', value: 'abc' }, ...]
  const useAutocompleteFilters = true;

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        setLoading(true);
        const response = await getLendersList();

        const apiLenders = response?.result?.lenders || response?.lenders || [];
        const transformedLenders = apiLenders.map(lender => ({
          id: lender.id,
          lenderId: lender.lender_lenderId,
          name: lender.name,
          mobileNumber: lender.mobileNumber,
          email: lender.email,
          institutionType: lender.lender_financialInstitutionType,
          location: lender.lender_location || lender.address || '',
          lendingType: lender.lender_typeOfLending,
          retailLoans: lender.lender_typeOfRetailLoans,
          designation: lender.lender_designation,
          verified: lender.lender_verified
        })) || [];
        setLenders(transformedLenders);
        setFilteredLenders(transformedLenders);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLenders();
  }, []);

  useEffect(() => {
    const filterLenders = () => {
      let filtered = lenders;
      if (activeFilters.length > 0) {
        filtered = filtered.filter(lender =>
          activeFilters.every(f => {
            const lenderFieldValue = (lender[f.field] || '').toString().toLowerCase();
            if (Array.isArray(f.value)) {
              if (f.value.length === 0) return true;
              return f.value.some(val =>
                lenderFieldValue.includes((val || '').toString().toLowerCase())
              );
            }
            const singleVal = (f.value || '').toString().toLowerCase();
            return singleVal ? lenderFieldValue.includes(singleVal) : true;
          })
        );
      }
      const query = '';
      if (query) {
        filtered = filtered.filter(lender =>
          lender.name?.toLowerCase().includes(query) ||
          lender.institutionType?.toLowerCase().includes(query) ||
          lender.location?.toLowerCase().includes(query) ||
          lender.lendingType?.toLowerCase().includes(query) ||
          lender.retailLoans?.toLowerCase().includes(query)
        );
      }
      setFilteredLenders(filtered);
      setPage(0);
    };
    filterLenders();
  }, [lenders, activeFilters]); 

  useEffect(() => {
    if (
      activeFilters.length > 0 &&
      filteredLenders.length > 0 &&
      (!selectedLenders ||
        filteredLenders.some(l => !selectedLenders.includes(l.lenderId)) ||
        selectedLenders.length !== filteredLenders.length)
    ) {
      onSelectionChange(filteredLenders.map(l => l.lenderId));
    }
  }, [filteredLenders]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredLenders.map(lender => lender.lenderId);
      onSelectionChange(newSelected);
    } else {
      onSelectionChange([]);
    }
  };

  const handleClick = (lenderId) => {
    const newSelected = selectedLenders.includes(lenderId)
      ? selectedLenders.filter(selectedId => selectedId !== lenderId)
      : [...selectedLenders, lenderId];
    
    onSelectionChange(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <TableWrapper>
      <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
        <div style={{ minWidth: 1300 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', px: 3, pt: 2.5, pb: 2 }}>
            {filterFields.map(field => {
              const options = Array.from(new Set(lenders.map(l => l[field.value]).filter(Boolean)));
              const selectedValues = activeFilters.find(f => f.field === field.value)?.value || [];
              const filterOptions = (opts, state) => {
                const query = (state.inputValue || '').toLowerCase();
                if (!query) return opts;
                const starts = [];
                const contains = [];
                opts.forEach(opt => {
                  const val = (opt || '').toString().toLowerCase();
                  if (val.startsWith(query)) starts.push(opt); else if (val.includes(query)) contains.push(opt);
                });
                return [...starts, ...contains];
              };
              return (
                useAutocompleteFilters ? (
                  <Autocomplete
                    key={field.value}
                    multiple
                    options={options}
                    value={selectedValues}
                    filterOptions={filterOptions}
                    onChange={(event, values) => {
                      setActiveFilters(prev => {
                        const others = prev.filter(f => f.field !== field.value);
                        const selected = Array.isArray(values) ? values : (values ? [values] : []);
                        return selected.length ? [...others, { field: field.value, value: selected }] : others;
                      });
                    }}
                    size="small"
                    disableCloseOnSelect
                    filterSelectedOptions
                    autoHighlight
                    openOnFocus
                    getOptionLabel={(option) => (option ?? '').toString()}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} placeholder={field.label} />
                    )}
                    sx={{ minWidth: 200, flex: '0 1 240px' }}
                  />
                ) : (
                  <Select
                    key={field.value}
                    displayEmpty
                    multiple
                    value={selectedValues}
                    onChange={e => {
                      const val = e.target.value;
                      setActiveFilters(prev => {
                        const others = prev.filter(f => f.field !== field.value);
                        const values = Array.isArray(val) ? val : (val ? [val] : []);
                        return values.length ? [...others, { field: field.value, value: values }] : others;
                      });
                    }}
                    size="small"
                    sx={{ minWidth: 160 }}
                    renderValue={selected => (selected.length === 0 ? field.label : selected.join(', '))}
                  >
                    <MenuItem value="" disabled><em>{field.label}</em></MenuItem>
                    {options.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                )
              );
            })}
            {activeFilters.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setActiveFilters([])}
                sx={{ height: 40 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </div>
        <TableHeader />

        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={32} />
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              Loading lenders...
            </Typography>
          </Box>
        ) : filteredLenders.length === 0 ? (
          <EmptyState>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
              No lenders available
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              We've added some test lenders for you to try out the functionality.
            </Typography>
          </EmptyState>
        ) : (
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedLenders.length > 0 && 
                      selectedLenders.length < filteredLenders.length
                    }
                    checked={
                      filteredLenders.length > 0 &&
                      selectedLenders.length === filteredLenders.length &&
                      filteredLenders.every(lender => selectedLenders.includes(lender.lenderId))
                    }
                    onChange={handleSelectAllClick}
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                  />
                </StyledTableCell>
                <StyledTableCell>User ID</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Mobile Number</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Institution Type</StyledTableCell>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>Type of Lending</StyledTableCell>
                <StyledTableCell>Retail Loans</StyledTableCell>
                <StyledTableCell>Designation</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLenders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((lender) => {
                  const isSelected = selectedLenders.includes(lender.lenderId);
                  return (
                    <TableRow
                      hover
                      onClick={() => handleClick(lender.lenderId)}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={-1}
                      key={lender.lenderId}
                      selected={isSelected}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                          }
                        }
                      }}
                    >
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                        />
                      </StyledTableCell>
                      <StyledTableCell>{lender.id}</StyledTableCell>
                      <StyledTableCell>{lender.name}</StyledTableCell>
                      <StyledTableCell>{lender.mobileNumber}</StyledTableCell>
                      <StyledTableCell>
                        <a href={`mailto:${lender.email}`} className="email">
                          {lender.email}
                        </a>
                      </StyledTableCell>
                      <StyledTableCell>{lender.institutionType}</StyledTableCell>
                      <StyledTableCell>{lender.location}</StyledTableCell>
                      <StyledTableCell>{lender.lendingType}</StyledTableCell>
                      <StyledTableCell>{lender.retailLoans}</StyledTableCell>
                      <StyledTableCell>{lender.designation}</StyledTableCell>
                      <StyledTableCell>
                        <Chip 
                          label={lender.verified ? "Verified" : "Unverified"} 
                          color={lender.verified ? "success" : "warning"}
                          size="small"
                          variant="outlined"
                        />
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
        
        {filteredLenders.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredLenders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(229, 232, 235, 0.7)',
              '.MuiTablePagination-select': {
                borderRadius: '6px',
              }
            }}
          />
        )}
      </TableContainer>
    </TableWrapper>
  );
};

export default LenderSelectionTable; 